import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import GlobalContext from "../../framework/GlobalContext";
import classes from "./UserReports.module.css";
import Shortcut from "../../framework/Shortcut";
import Autocomplete from "../../framework/Autocomplete";
import Button from "../../framework/Button";
import Reports from "../../common/reports/Reports";
import UserReportsSummary from "./UserReportsSummary";
import AddUsers from "./AddUsers";
import { saveUser } from "../../services/user-service";
import { displayToast } from "../../framework/toast/toastUtils";
import { createFilterParam } from "../../common/reports/reportUtils";
import { BOOL, EQ } from "../../common/reports/reportConstants";
import UserLogs from "./UserLogs";
import moment from "moment";
import { getISODateTime, isEmpty } from "../../utils/utils";

const UserReports = () => {
  const defaultFilter = useMemo(() => {
    return {
      filters: [createFilterParam("deleted", false, BOOL, EQ)],
      limit: 100,
      orderBy: [{ fieldName: "userId" }],
      export: false,
    };
  }, []);
  const { setCurrentComponent, currentComponent, displayModal } =
    useContext(GlobalContext);
  const reportRef = useRef();
  const [rowLevelCTAs, setRowLevelCTAs] = useState([]);
  const [showAddUserAC, setShowAddUserAC] = useState(false);
  const [activateBtnLabel, setActivateBtnLabel] = useState("Activate");
  const [showLogs, setShowLogs] = useState(false);
  const [userId, setUserId] = useState(null);

  const editRef = useRef();
  const delRef = useRef();
  const logRef = useRef();
  const deactivateRef = useRef();

  const openAddUserAC = useCallback(() => {
    setShowAddUserAC(true);
  }, []);
  const updateActivateBtnLabel = useCallback(({ ttl }) => {
    if (!isEmpty(ttl) && moment(ttl).isSameOrBefore(moment())) {
      setActivateBtnLabel("Activate");
    } else {
      setActivateBtnLabel("Deactivate");
    }
  }, []);
  const closeAddUserAC = useCallback(() => {
    setCurrentComponent("grid_reports");
    setShowAddUserAC(false);
    setUserId(null);
    updateActivateBtnLabel({
      ttl: reportRef?.current?.getCurrentRowData()?.ttl,
    });
  }, [setCurrentComponent, updateActivateBtnLabel]);
  const closeShowLogsAC = useCallback(() => {
    setCurrentComponent("grid_reports");
    setShowLogs(false);
    setUserId(null);
  }, [setCurrentComponent]);
  const refreshData = useCallback(() => {
    reportRef.current.applyFilters(defaultFilter);
  }, [defaultFilter]);
  const deleteHandler = useCallback(async () => {
    if (
      await displayModal({
        type: "warn",
        title: "Are you sure you want to delete the record?",
      })
    ) {
      saveUser({
        deleted: true,
        userId: reportRef.current.getCurrentRowData()?.userId,
      }).then((r) => {
        displayToast({
          type: "success",
          title: "User deleted successfully.",
        });
        refreshData();
      });
    }
  }, [displayModal, refreshData]);
  const editUserHandler = useCallback(() => {
    setUserId(reportRef.current.getCurrentRowData()?._id);
    setShowAddUserAC(true);
  }, []);
  const logHandler = useCallback(() => {
    setUserId(reportRef.current.getCurrentRowData()?.userId);
    setShowLogs(true);
  }, []);
  // const saveUserHandler = useCallback(
  //   async (data) => refreshData(),
  //   [refreshData]
  // );
  const listener = useCallback(
    (e) => {
      const key = e.key?.toLowerCase();
      const ctrlKey = e.ctrlKey;
      const altKey = e.altKey;
      const currentRow = reportRef.current.getCurrentRowData();
      if (ctrlKey && key === "i") {
        if (currentRow) editRef.current.click();
        return;
      }
      if (ctrlKey && key === "u") {
        openAddUserAC();
        return;
      }
      if (altKey && key === "r") {
        return refreshData();
      }
      if (altKey && key === "e") {
        return quickExportHandler();
      }
      if (key === "delete") {
        if (currentRow) delRef.current.click();
        return;
      }
      if (key === "enter") {
        if (currentRow) logRef.current.click();
        return;
      }
      if (ctrlKey && key === "d") {
        deactivateRef.current.click();
        return;
      }
    },
    [openAddUserAC, refreshData]
  );
  const quickExportHandler = () => {
    reportRef?.current?.quickExport({ fileName: "UserReports" });
  };
  const deactivateHandler = useCallback(() => {
    let date = moment(reportRef.current.getCurrentRowData().ttl).format(
      "DD/MM/YYYY"
    );
    const payload = {
      userId: reportRef.current.getCurrentRowData().userId,
      ttl: moment(date).isSameOrBefore(moment().format("DD/MM/YYYY"))
        ? ""
        : getISODateTime(moment().format("DD/MM/YYYY")), //moment().format("DD/MM/YYYY"),
    };
    return saveUser(payload).then((r) => {
      refreshData();
      updateActivateBtnLabel({ ttl: r.ttl });
      return displayToast({
        type: "success",
        title: "User status updated.",
      });
    });
  }, [refreshData, updateActivateBtnLabel]);
  useEffect(() => {
    const allRowCTAs = [
      <Button
        startIcon={<Shortcut keys={["Ctrl", "I"]} />}
        label="Edit"
        onClick={editUserHandler}
        ref={editRef}
      />,
      <Button
        onClick={deleteHandler}
        startIcon={<Shortcut keys={["Del"]} />}
        label="Delete"
        ref={delRef}
      />,
      <Button
        onClick={logHandler}
        startIcon={<Shortcut keys={["Enter"]} />}
        label="View Log"
        ref={logRef}
      />,
      // <Button
      //   onClick={deactivateHandler}
      //   startIcon={<Shortcut keys={["Ctrl", "D"]} />}
      //   label={activateBtnLabel}
      //   ref={deactivateRef}
      // />,
    ];
    setRowLevelCTAs(allRowCTAs);
  }, [
    activateBtnLabel,
    deactivateHandler,
    deleteHandler,
    editUserHandler,
    logHandler,
  ]);
  useEffect(() => {
    if (currentComponent === "grid_reports") {
      document.addEventListener("keyup", listener, false);
    }
    return () => {
      document.removeEventListener("keyup", listener, false);
    };
  }, [currentComponent, listener]);
  useEffect(() => {
    refreshData();
  }, [refreshData]);
  return (
    <div className={classes["user-report-container"]}>
      <Reports
        identifier="user"
        rowLevelCTAs={rowLevelCTAs}
        reportRef={reportRef}
        showFilterCTA={false}
        onRowDown={(data) => updateActivateBtnLabel({ ttl: data.ttl })}
        onRowUp={(data) => updateActivateBtnLabel({ ttl: data.ttl })}
      />
      <UserReportsSummary
        refreshData={refreshData}
        showAutoComplete={openAddUserAC}
        quickExport={quickExportHandler}
      />
      <Autocomplete
        show={showAddUserAC}
        width="85%"
        height="75%"
        closeAutoComplete={closeAddUserAC}
      >
        <AddUsers
          id={userId}
          onSaveUser={(data) => {
            setCurrentComponent("grid_reports");
            setShowAddUserAC(false);
            refreshData();
          }}
          onEscape={closeAddUserAC}
        />
      </Autocomplete>
      <Autocomplete
        show={showLogs}
        width="85%"
        height="60%"
        closeAutoComplete={closeShowLogsAC}
      >
        <UserLogs id={userId} onEscape={closeShowLogsAC} />
      </Autocomplete>
    </div>
  );
};

export default UserReports;
